import { useEffect, useMemo, useState } from "react";
import { api, fmt, formatErr, downloadCSV } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Filter as FilterIcon, FileSpreadsheet, AlertTriangle, X, CalendarIcon } from "lucide-react";
import { PageHeader } from "@/components/EntityPage";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as UICalendar } from "@/components/ui/calendar";

const PALETTE = [
  { bg: "bg-amber-500/25", border: "border-amber-500/50", text: "text-amber-100" },
  { bg: "bg-sky-500/25", border: "border-sky-500/50", text: "text-sky-100" },
  { bg: "bg-emerald-500/25", border: "border-emerald-500/50", text: "text-emerald-100" },
  { bg: "bg-purple-500/25", border: "border-purple-500/50", text: "text-purple-100" },
  { bg: "bg-pink-500/25", border: "border-pink-500/50", text: "text-pink-100" },
  { bg: "bg-teal-500/25", border: "border-teal-500/50", text: "text-teal-100" },
  { bg: "bg-indigo-500/25", border: "border-indigo-500/50", text: "text-indigo-100" },
  { bg: "bg-orange-500/25", border: "border-orange-500/50", text: "text-orange-100" },
];
function colorFor(id) {
  if (!id) return PALETTE[0];
  let h = 0; for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return PALETTE[h % PALETTE.length];
}

// ---- Date helpers (form uses dd-mm-yyyy; API uses yyyy-mm-dd) ----
function isoToDMY(iso) {
  if (!iso) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : iso;
}
function dmyToISO(dmy) {
  if (!dmy) return "";
  const m = /^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/.exec(String(dmy).trim());
  if (!m) return "";
  const [_, d, mo, y] = m;
  const day = d.padStart(2, "0"), mm = mo.padStart(2, "0");
  return `${y}-${mm}-${day}`;
}
function isValidDMY(dmy) {
  const iso = dmyToISO(dmy);
  if (!iso) return false;
  const dt = new Date(iso);
  return !isNaN(dt) && iso === dt.toISOString().slice(0, 10);
}

function DMYInput({ value, onChange, required, testid }) {
  const [text, setText] = useState(isoToDMY(value));
  const [open, setOpen] = useState(false);
  useEffect(() => { setText(isoToDMY(value)); }, [value]);
  const good = !text || isValidDMY(text);
  const selected = value && isValidDMY(isoToDMY(value)) ? (() => {
    const [y, m, d] = value.split("-").map(Number);
    return new Date(y, m - 1, d);
  })() : undefined;
  return (
    <div className="flex mt-1 gap-1">
      <Input
        type="text"
        inputMode="numeric"
        placeholder="DD-MM-YYYY"
        required={required}
        value={text}
        onChange={(e) => {
          let raw = e.target.value.replace(/[^0-9]/g, "").slice(0, 8);
          let out = raw;
          if (raw.length > 4) out = `${raw.slice(0,2)}-${raw.slice(2,4)}-${raw.slice(4)}`;
          else if (raw.length > 2) out = `${raw.slice(0,2)}-${raw.slice(2)}`;
          setText(out);
          const iso = dmyToISO(out);
          onChange(iso || out);
        }}
        data-testid={testid}
        className={`bg-[hsl(224_40%_12%)] flex-1 ${!good ? "border-red-500/60" : ""}`}
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" size="icon" data-testid={`${testid}-picker`}
            className="border-border bg-[hsl(224_40%_12%)] hover:bg-amber-500/10 hover:text-amber-300 shrink-0">
            <CalendarIcon className="w-4 h-4"/>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 bg-[hsl(224_45%_9%)] border-border" align="end">
          <UICalendar
            mode="single"
            selected={selected}
            onSelect={(d) => {
              if (!d) return;
              const iso = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
              setText(isoToDMY(iso));
              onChange(iso);
              setOpen(false);
            }}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

function EntryForm({ initial, refs, defaultDate, lockVehicleId, onSubmit }) {
  const [f, setF] = useState(
    initial ||
    { start_date: defaultDate || "", end_date: defaultDate || "", vehicle_id: lockVehicleId || "" }
  );
  useEffect(() => {
    if (!initial && lockVehicleId) setF((s) => ({ ...s, vehicle_id: lockVehicleId }));
  }, [initial, lockVehicleId]);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const num = (k) => (e) => set(k, parseFloat(e.target.value) || 0);
  const vehicleLocked = !!lockVehicleId && !initial;
  const lockedVehicle = refs.vehicles.find((v) => v.id === (lockVehicleId || f.vehicle_id));

  function submit(e) {
    e.preventDefault();
    // Validate dates
    if (!isValidDMY(isoToDMY(f.start_date)) || !isValidDMY(isoToDMY(f.end_date))) {
      toast.error("Please enter dates in DD-MM-YYYY format"); return;
    }
    if (f.start_date > f.end_date) { toast.error("From date must be before To date"); return; }
    onSubmit(f);
  }

  return (
    <form onSubmit={submit} className="space-y-3 max-h-[70vh] overflow-y-auto pr-2">
      <div className="grid grid-cols-2 gap-3">
        {!vehicleLocked && (
          <div><Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Vehicle</Label>
            <Select value={f.vehicle_id || ""} onValueChange={(v) => set("vehicle_id", v)}>
              <SelectTrigger data-testid="cal-form-vehicle" className="bg-[hsl(224_40%_12%)] mt-1"><SelectValue placeholder="Select vehicle"/></SelectTrigger>
              <SelectContent>{refs.vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.registration_no} · {v.vehicle_name}</SelectItem>)}</SelectContent>
            </Select></div>
        )}
        {vehicleLocked && lockedVehicle && (
          <div className="col-span-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2" data-testid="cal-form-vehicle-locked">
            <div className="text-[10px] uppercase tracking-wider text-amber-300">Vehicle</div>
            <div className="font-mono text-amber-200 text-sm mt-0.5">{lockedVehicle.registration_no} · {lockedVehicle.vehicle_name}</div>
          </div>
        )}
        <div><Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Driver</Label>
          <Select value={f.driver_id || ""} onValueChange={(v) => set("driver_id", v)}>
            <SelectTrigger data-testid="cal-form-driver" className="bg-[hsl(224_40%_12%)] mt-1"><SelectValue placeholder="Select driver"/></SelectTrigger>
            <SelectContent>{refs.drivers.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Client</Label>
          <Select value={f.client_id || ""} onValueChange={(v) => set("client_id", v)}>
            <SelectTrigger className="bg-[hsl(224_40%_12%)] mt-1"><SelectValue placeholder="Select client"/></SelectTrigger>
            <SelectContent>{refs.clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Trip value ₹</Label>
          <Input type="number" value={f.trip_value || 0} onChange={num("trip_value")} className="bg-[hsl(224_40%_12%)] mt-1"/></div>
        <div><Label className="text-[10px] uppercase tracking-wider text-muted-foreground">From date (DD-MM-YYYY)</Label>
          <DMYInput required value={f.start_date} onChange={(iso) => set("start_date", iso)} testid="cal-form-start"/></div>
        <div><Label className="text-[10px] uppercase tracking-wider text-muted-foreground">To date (DD-MM-YYYY)</Label>
          <DMYInput required value={f.end_date} onChange={(iso) => set("end_date", iso)} testid="cal-form-end"/></div>
        <div><Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Advance ₹</Label>
          <Input type="number" value={f.advance_paid || 0} onChange={num("advance_paid")} className="bg-[hsl(224_40%_12%)] mt-1"/></div>
        <div><Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Balance ₹</Label>
          <Input type="number" value={f.remaining_balance || 0} onChange={num("remaining_balance")} className="bg-[hsl(224_40%_12%)] mt-1"/></div>
      </div>
      <div><Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Remarks</Label>
        <Input value={f.remarks || ""} onChange={(e) => set("remarks", e.target.value)} className="bg-[hsl(224_40%_12%)] mt-1"/></div>
      <div className="flex justify-end">
        <Button type="submit" data-testid="cal-form-save" className="bg-amber-500 text-[hsl(224_60%_6%)] hover:bg-amber-400 font-semibold">{initial ? "Update" : "Assign"}</Button>
      </div>
    </form>
  );
}

export default function CalendarPage() {
  const [entries, setEntries] = useState([]);
  const [refs, setRefs] = useState({ vehicles: [], drivers: [], clients: [] });
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [defaultDate, setDefaultDate] = useState("");
  const [lockVehicleId, setLockVehicleId] = useState("");
  const [dayView, setDayView] = useState(null);
  const [fDriver, setFDriver] = useState("all");
  const [fVehicle, setFVehicle] = useState("all");

  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10);
  const lastOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().slice(0, 10);
  const [fFrom, setFFrom] = useState(firstOfMonth);
  const [fTo, setFTo] = useState(lastOfMonth);

  async function load() {
    const [e, v, d, c] = await Promise.all([
      api.get("/calendar"), api.get("/vehicles"), api.get("/drivers"), api.get("/clients"),
    ]);
    setEntries(e.data);
    setRefs({ vehicles: v.data, drivers: d.data, clients: c.data });
  }
  useEffect(() => { load(); }, []);

  const nameOf = (arr, id, key = "name") => arr.find((x) => x.id === id)?.[key] || "";

  const scoped = useMemo(() => entries.filter((e) => {
    if (fDriver !== "all" && e.driver_id !== fDriver) return false;
    if (fVehicle !== "all" && e.vehicle_id !== fVehicle) return false;
    if (fFrom && e.end_date && e.end_date < fFrom) return false;
    if (fTo && e.start_date && e.start_date > fTo) return false;
    return true;
  }), [entries, fDriver, fVehicle, fFrom, fTo]);

  const days = useMemo(() => {
    if (!fFrom || !fTo || fFrom > fTo) return [];
    const out = [];
    const [sy, sm, sd] = fFrom.split("-").map(Number);
    const [ey, em, ed] = fTo.split("-").map(Number);
    let cur = new Date(sy, sm - 1, sd);
    const stop = new Date(ey, em - 1, ed);
    while (cur <= stop && out.length < 200) {
      out.push(`${cur.getFullYear()}-${String(cur.getMonth()+1).padStart(2,"0")}-${String(cur.getDate()).padStart(2,"0")}`);
      cur = new Date(cur.getFullYear(), cur.getMonth(), cur.getDate() + 1);
    }
    return out;
  }, [fFrom, fTo]);

  const vehiclesToShow = useMemo(() => {
    if (fVehicle !== "all") return refs.vehicles.filter((v) => v.id === fVehicle);
    return refs.vehicles;
  }, [refs.vehicles, fVehicle]);

  const dateMap = useMemo(() => {
    const m = new Map();
    scoped.forEach((e) => {
      if (!e.start_date || !e.end_date) return;
      const [sy, sm, sd] = e.start_date.split("-").map(Number);
      const [ey, em, ed] = e.end_date.split("-").map(Number);
      let cur = new Date(sy, sm - 1, sd);
      const stop = new Date(ey, em - 1, ed);
      while (cur <= stop) {
        const iso = `${cur.getFullYear()}-${String(cur.getMonth()+1).padStart(2,"0")}-${String(cur.getDate()).padStart(2,"0")}`;
        const key = `${e.vehicle_id}|${iso}`;
        if (!m.has(key)) m.set(key, []);
        // guard against duplicate insertions of the same entry
        const arr = m.get(key);
        if (!arr.some((x) => x.id === e.id)) arr.push(e);
        cur = new Date(cur.getFullYear(), cur.getMonth(), cur.getDate() + 1);
      }
    });
    return m;
  }, [scoped]);

  const conflicts = useMemo(() => {
    const set = new Set();
    dateMap.forEach((list, k) => { if (list.length > 1) set.add(k); });
    return set;
  }, [dateMap]);

  async function save(f) {
    try {
      const c = refs.clients.find((x) => x.id === f.client_id);
      const payload = { ...f, client_name: c?.name || f.client_name };
      if (editing) await api.put(`/calendar/${editing.id}`, payload);
      else await api.post("/calendar", payload);
      toast.success(editing ? "Updated" : "Assigned");
      setFormOpen(false); setEditing(null); setDefaultDate(""); setLockVehicleId("");
      await load();
      setDayView(null);
    } catch (err) { toast.error(formatErr(err.response?.data?.detail) || "Failed"); }
  }
  async function del(id) {
    if (!window.confirm("Delete this assignment?")) return;
    try { await api.delete(`/calendar/${id}`); toast.success("Deleted"); await load(); setDayView(null); }
    catch (err) { toast.error(formatErr(err.response?.data?.detail) || "Failed"); }
  }

  function openCellForm(vehicleId, date) {
    setEditing(null);
    setLockVehicleId(vehicleId);
    setDefaultDate(date);
    setFormOpen(true);
  }

  function openDayModal(vehicleId, date) {
    const key = `${vehicleId}|${date}`;
    const list = dateMap.get(key) || [];
    setDayView({ date, vehicleId, entries: list, conflict: list.length > 1 });
  }

  function exportExcel() {
    const rows = scoped.map((e) => ({
      Vehicle: nameOf(refs.vehicles, e.vehicle_id, "registration_no"),
      Driver: nameOf(refs.drivers, e.driver_id),
      Client: e.client_name || nameOf(refs.clients, e.client_id),
      From: isoToDMY(e.start_date), To: isoToDMY(e.end_date),
      "Trip Value": e.trip_value || 0,
      Advance: e.advance_paid || 0,
      Balance: e.remaining_balance || 0,
      Remarks: e.remarks || "",
    }));
    if (!rows.length) return toast.error("No data to export");
    downloadCSV(rows, "calendar.csv");
    toast.success("Downloaded");
  }

  return (
    <div>
      <PageHeader
        title="Assignment Calendar"
        subtitle="Vehicle × Date grid. Click any coloured date to view, edit or delete the assignment. Red = booking conflict on same vehicle."
        right={
          <>
            <Button variant="outline" onClick={exportExcel} data-testid="cal-export" className="border-border bg-[hsl(224_40%_12%)] gap-2"><FileSpreadsheet className="w-4 h-4"/> Excel</Button>
            <Dialog open={formOpen} onOpenChange={(v) => { setFormOpen(v); if (!v) { setEditing(null); setDefaultDate(""); setLockVehicleId(""); } }}>
              <DialogTrigger asChild>
                <Button data-testid="cal-add" onClick={() => { setLockVehicleId(""); setDefaultDate(""); setEditing(null); }} className="bg-amber-500 text-[hsl(224_60%_6%)] hover:bg-amber-400 font-semibold gap-2"><Plus className="w-4 h-4"/> Assign</Button>
              </DialogTrigger>
              <DialogContent className="bg-[hsl(224_45%_9%)] border-border max-w-2xl">
                <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Edit assignment" : "New assignment"}</DialogTitle></DialogHeader>
                <EntryForm initial={editing} defaultDate={defaultDate} lockVehicleId={lockVehicleId} refs={refs} onSubmit={save}/>
              </DialogContent>
            </Dialog>
          </>
        }
      />

      <Card className="p-5 bg-[hsl(224_45%_9%)] border-border mb-4">
        <div className="flex items-center gap-2 mb-4 text-amber-400">
          <FilterIcon className="w-4 h-4"/><div className="text-xs uppercase tracking-[0.2em]">Filters</div>
          {conflicts.size > 0 && (
            <Badge className="ml-3 bg-red-500/20 text-red-300 border-red-500/40 gap-1" data-testid="conflict-badge">
              <AlertTriangle className="w-3 h-3"/> {conflicts.size} conflict date{conflicts.size > 1 ? "s" : ""}
            </Badge>
          )}
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <Label className="text-[10px] uppercase text-muted-foreground">Driver</Label>
            <Select value={fDriver} onValueChange={setFDriver}>
              <SelectTrigger data-testid="cal-f-driver" className="bg-[hsl(224_40%_12%)] mt-1"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All drivers</SelectItem>
                {refs.drivers.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase text-muted-foreground">Vehicle</Label>
            <Select value={fVehicle} onValueChange={setFVehicle}>
              <SelectTrigger data-testid="cal-f-vehicle" className="bg-[hsl(224_40%_12%)] mt-1"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All vehicles</SelectItem>
                {refs.vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.registration_no} · {v.vehicle_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase text-muted-foreground">From date</Label>
            <Input type="date" value={fFrom} onChange={(e) => setFFrom(e.target.value)} data-testid="cal-f-from" className="bg-[hsl(224_40%_12%)] mt-1"/>
          </div>
          <div>
            <Label className="text-[10px] uppercase text-muted-foreground">To date</Label>
            <Input type="date" value={fTo} onChange={(e) => setFTo(e.target.value)} data-testid="cal-f-to" className="bg-[hsl(224_40%_12%)] mt-1"/>
          </div>
        </div>
      </Card>

      <Card className="bg-[hsl(224_45%_9%)] border-border overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead>
            <tr>
              <th className="sticky left-0 bg-[hsl(224_40%_11%)] p-2 text-left uppercase tracking-wider text-[10px] text-muted-foreground border-r border-border min-w-[180px] z-10">Vehicle</th>
              {days.map((d) => {
                const [y, m, dd] = d.split("-").map(Number);
                const dt = new Date(y, m - 1, dd);
                const wd = dt.toLocaleDateString("en-US", { weekday: "short" });
                return (
                  <th key={d} className="p-1 text-center font-mono text-muted-foreground border-r border-border/40 min-w-[36px]">
                    <div className="text-[10px]">{dd}</div>
                    <div className="text-[9px] text-slate-500">{wd[0]}</div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {vehiclesToShow.length === 0 || days.length === 0 ? (
              <tr><td colSpan={days.length + 1} className="p-6 text-center text-muted-foreground">Add vehicles and pick a date range.</td></tr>
            ) : vehiclesToShow.map((v) => (
              <tr key={v.id} className="border-t border-border">
                <td className="sticky left-0 bg-[hsl(224_45%_9%)] p-2 border-r border-border z-10">
                  <div className="font-mono text-amber-300 text-xs">{v.registration_no}</div>
                  <div className="text-[10px] text-muted-foreground truncate max-w-[160px]">{v.vehicle_name}</div>
                </td>
                {days.map((d) => {
                  const key = `${v.id}|${d}`;
                  const list = dateMap.get(key) || [];
                  const isConflict = list.length > 1;
                  const label = list.length === 1
                    ? ((list[0].client_name || nameOf(refs.clients, list[0].client_id) || "?").split(" ")[0].slice(0, 3))
                    : (list.length > 1 ? `${list.length}!` : "");
                  const clr = list.length === 0 ? null : colorFor(list[0].id);
                  return (
                    <td key={d} className="p-1 border-r border-border/40 text-center align-middle">
                      {list.length === 0 ? (
                        <button
                          onClick={() => openCellForm(v.id, d)}
                          className="h-7 w-full rounded bg-white/[0.02] hover:bg-amber-500/10 transition-colors"
                          title={`Assign ${v.registration_no} on ${isoToDMY(d)}`}
                          data-testid={`cell-${v.id}-${d}`}
                        />
                      ) : (
                        <button
                          data-testid={`booked-${v.id}-${d}`}
                          onClick={() => openDayModal(v.id, d)}
                          title={isConflict ? "Booking conflict — click to resolve" : `${list[0].client_name || "Trip"} — click for details`}
                          className={`h-7 w-full rounded border grid place-items-center text-[9px] font-medium transition-transform hover:scale-[1.05] ${
                            isConflict
                              ? "bg-red-500/30 border-red-500/60 text-red-100 animate-pulse"
                              : `${clr.bg} ${clr.border} ${clr.text}`
                          }`}
                        >
                          {label}
                        </button>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        <div className="p-3 border-t border-border flex flex-wrap items-center gap-4 text-[11px] text-muted-foreground">
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-red-500/30 border border-red-500/60"/> Conflict — adjust another vehicle</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-amber-500/25 border border-amber-500/50"/> Trip A</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-sky-500/25 border border-sky-500/50"/> Trip B</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-emerald-500/25 border border-emerald-500/50"/> Trip C …</span>
        </div>
      </Card>

      <Dialog open={!!dayView} onOpenChange={(v) => { if (!v) setDayView(null); }}>
        <DialogContent className="bg-[hsl(224_45%_9%)] border-border max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              Bookings on {isoToDMY(dayView?.date)}
              {dayView?.conflict && <Badge className="bg-red-500/20 text-red-300 border-red-500/40 gap-1"><AlertTriangle className="w-3 h-3"/> Conflict</Badge>}
            </DialogTitle>
          </DialogHeader>
          {dayView?.conflict && (
            <div className="p-3 rounded-md border border-red-500/40 bg-red-500/10 text-red-200 text-sm flex items-start gap-2" data-testid="conflict-msg">
              <AlertTriangle className="w-4 h-4 mt-0.5"/>
              <div>
                <strong>Booking conflict on this date.</strong> Please adjust another vehicle on {isoToDMY(dayView?.date)} — this vehicle already has multiple trips overlapping.
              </div>
            </div>
          )}
          <div className="space-y-3">
            {dayView?.entries?.length === 0 && <div className="text-sm text-muted-foreground">No bookings on this day.</div>}
            {dayView?.entries?.map((e) => {
              const clr = colorFor(e.id);
              return (
                <div key={e.id} data-testid={`day-entry-${e.id}`} className={`p-4 rounded-lg border ${clr.bg} ${clr.border}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="text-xs uppercase tracking-wider text-muted-foreground">Client</div>
                      <div className="font-semibold">{e.client_name || nameOf(refs.clients, e.client_id) || "—"}</div>
                    </div>
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" data-testid={`day-edit-${e.id}`} className="h-8 w-8 hover:bg-white/10" onClick={() => { setEditing(e); setLockVehicleId(""); setDefaultDate(""); setDayView(null); setFormOpen(true); }}>
                        <Pencil className="w-3.5 h-3.5"/>
                      </Button>
                      <Button size="icon" variant="ghost" data-testid={`day-del-${e.id}`} className="h-8 w-8 hover:bg-red-500/10 hover:text-red-400" onClick={() => del(e.id)}>
                        <Trash2 className="w-3.5 h-3.5"/>
                      </Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3 text-sm">
                    <div><div className="text-[10px] uppercase text-muted-foreground">From → To</div><div className="font-mono">{isoToDMY(e.start_date)} → {isoToDMY(e.end_date)}</div></div>
                    <div><div className="text-[10px] uppercase text-muted-foreground">Driver</div><div>{nameOf(refs.drivers, e.driver_id) || "—"}</div></div>
                    <div><div className="text-[10px] uppercase text-muted-foreground">Trip value</div><div className="font-mono text-sky-300">{fmt(e.trip_value)}</div></div>
                    <div><div className="text-[10px] uppercase text-muted-foreground">Advance</div><div className="font-mono text-emerald-300">{fmt(e.advance_paid)}</div></div>
                    <div><div className="text-[10px] uppercase text-muted-foreground">Balance</div><div className="font-mono text-amber-300">{fmt(e.remaining_balance)}</div></div>
                    <div><div className="text-[10px] uppercase text-muted-foreground">Remarks</div><div className="truncate">{e.remarks || "—"}</div></div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex justify-end pt-2">
            <Button variant="outline" onClick={() => setDayView(null)} className="border-border bg-[hsl(224_40%_12%)] gap-2"><X className="w-4 h-4"/> Close</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
